import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

/** One business category a lead carries, as the record and the picker show it. */
export interface LeadBusinessType {
  id: number;
  name: string;
}

export interface LeadProfile {
  id: number;
  name: string | null;
  leadStageId: number;
  leadStageName: string;
  salesRepUserId: number | null;
  /** What kind of business this is. A closed list, and a lead may be several. */
  businessTypes: LeadBusinessType[];
  origin: string;
  addressLine1: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  latitude: number | null;
  longitude: number | null;
  probability: number | null;
  expectedAnnualRevenue: number | null;
  expectedAnnualContribution: number | null;
  nextActionType: string | null;
  nextActionDue: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * The write side names the categories by id, because the client picked them
 * from a list the server gave it - and the read side carries the names too, so
 * a screen showing a lead never has to look one up.
 */
export type SaveLeadProfile = Omit<
  LeadProfile,
  'id' | 'leadStageName' | 'createdAt' | 'updatedAt' | 'businessTypes'
> & { businessTypeIds: number[] };

export interface DeleteAllLeadDataResult {
  deletedCount: number;
}

@Injectable({ providedIn: 'root' })
export class LeadApiService {
  constructor(private readonly http: HttpClient) {}

  getProfile(id: number): Observable<LeadProfile> {
    return this.http.get<LeadProfile>(`/api/leads/${id}/profile`);
  }

  create(profile: SaveLeadProfile): Observable<{ id: number }> {
    return this.http.post<{ id: number }>('/api/leads', profile);
  }

  update(id: number, profile: SaveLeadProfile): Observable<{ id: number }> {
    return this.http.put<{ id: number }>(`/api/leads/${id}`, profile);
  }

  deleteAllLeads(): Observable<DeleteAllLeadDataResult> {
    return this.http.delete<DeleteAllLeadDataResult>('/api/leads', {
      body: { confirmation: 'DELETE' },
    });
  }

}
